import { z } from 'zod'

/** Cursor pagination (ARCHITECTURE §7, P-REC-001). */
export const PAGE_LIMIT_DEFAULT = 50
export const PAGE_LIMIT_MAX = 200

export const pageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(PAGE_LIMIT_DEFAULT),
  cursor: z.string().max(1024).optional(),
})
export type PageQuery = z.infer<typeof pageQuerySchema>

export function pageSchema<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), next_cursor: z.string().nullable() })
}

export interface Page<T> {
  items: T[]
  next_cursor: string | null
}

/** Keyset position: the sort value and the id of the last row of a page. */
export type CursorKey = readonly [string | number, string]

function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
  return new TextDecoder().decode(Uint8Array.from(binary, (ch) => ch.charCodeAt(0)))
}

export function encodeCursor(key: CursorKey): string {
  return toBase64Url(JSON.stringify(key))
}

/** Returns null for any malformed cursor; callers turn that into a 422. */
export function decodeCursor(cursor: string): CursorKey | null {
  try {
    const value: unknown = JSON.parse(fromBase64Url(cursor))
    if (
      Array.isArray(value) &&
      value.length === 2 &&
      (typeof value[0] === 'string' || typeof value[0] === 'number') &&
      typeof value[1] === 'string'
    ) {
      return [value[0], value[1]]
    }
    return null
  } catch {
    return null
  }
}
